import React, { useCallback, useRef, useState } from 'react';
import cl from 'classnames';

import { IconsEnum } from '../../../../../../../assets/icons/types';
import { Icon } from '../../../../../../../helpers/Icon';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { StaticImageHelper } from '../../../../../../../helpers/StaticImageHelper';
import { LoadingSpinner } from '../../../../../../../helpers/LoadingSpinner';
import { TextareaAutosizeInput } from '../../../../../../../helpers/TextareaAutosizeInput';
import { Translate } from '../../../../../../../helpers/Translate';

import { modelViewersKeys, words } from '../../../../../../../locales/keys';

interface EditPromptCardReferenceItem {
  id: string;
  localUrl: string;
  isUploading: boolean;
}

interface ModelViewerImageEditPromptCardProps {
  prompt: string;
  isGenerating: boolean;
  generateError: string | null;
  references: EditPromptCardReferenceItem[];
  isUploadingReference: boolean;
  onPromptChange: (value: string) => void;
  onReferenceFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onReferenceFileDrop: (file: File) => void;
  onRemoveReference: (id: string) => void;
  onGenerate: () => void;
  onMinimize?: () => void;
}

function ModelViewerImageEditPromptCard({
  prompt,
  isGenerating,
  generateError,
  references,
  isUploadingReference,
  onPromptChange,
  onReferenceFileChange,
  onReferenceFileDrop,
  onRemoveReference,
  onGenerate,
  onMinimize
}: ModelViewerImageEditPromptCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isReferenceDragging, setIsReferenceDragging] = useState(false);
  const referenceDragCounter = useRef(0);

  const handleReferenceDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    referenceDragCounter.current++;
    setIsReferenceDragging(true);
  }, []);

  const handleReferenceDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleReferenceDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    referenceDragCounter.current--;
    if (referenceDragCounter.current <= 0) {
      referenceDragCounter.current = 0;
      setIsReferenceDragging(false);
    }
  }, []);

  const handleReferenceDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      referenceDragCounter.current = 0;
      setIsReferenceDragging(false);
      Array.from(e.dataTransfer.files ?? [])
        .filter((file) => file.type.startsWith('image/'))
        .forEach((file) => onReferenceFileDrop(file));
    },
    [onReferenceFileDrop]
  );

  const isDisabled = isGenerating || isUploadingReference;

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
    }
  };

  return (
    <div
      ref={containerRef}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onDragEnter={handleReferenceDragEnter}
      onDragOver={handleReferenceDragOver}
      onDragLeave={handleReferenceDragLeave}
      onDrop={handleReferenceDrop}
      className={cl(
        'group/message-editor border rounded-lg absolute bottom-16 left-1/2 -translate-x-1/2 w-[min(520px,calc(100%-6rem))] bg-white/75 dark:bg-gray-900/75 backdrop-blur-sm z-40',
        isFocused || isReferenceDragging
          ? 'ring-4 ring-purple-500/20 border-purple-400 dark:border-purple-500'
          : 'border-gray-200 dark:border-gray-700'
      )}
    >
      {isReferenceDragging && (
        <div className="absolute inset-0 z-10 rounded-lg flex items-center justify-center bg-purple-500/10 border-2 border-dashed border-purple-400 dark:border-purple-500 pointer-events-none">
          <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
            <Translate id={modelViewersKeys.addReference} />
          </p>
        </div>
      )}

      {onMinimize && (
        <div className="absolute bottom-full left-0 right-0 flex justify-end items-end pb-1 pointer-events-none">
          <PureIconButtonHelper
            icon={IconsEnum.WINDOW_MINIMIZE_OUTLINE}
            iconClassName="h-5 w-5"
            className="py-1 pl-1 pr-1 rounded-md inline-flex items-center leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 scale-0 group-hover/message-editor:scale-100 transition-transform duration-100 pointer-events-auto transition-colors duration-150 ease-out"
            onClick={onMinimize}
          />
        </div>
      )}

      <div className="relative grid grid-cols-[1fr_auto]">
        {/* Fixed edit command header — no dismiss button */}
        <div className="col-span-full py-1 px-2 dark text-white flex gap-2 rounded-t-md bg-purple-600/60 dark:bg-purple-600/60">
          <Icon icon={IconsEnum.COMMAND_OUTLINE} className="h-4 w-4" />
          <span className="leading-4 text-xs">
            <Translate id={modelViewersKeys.generationModeEdit} />
          </span>
          <span className="bg-purple-800/30 dark:bg-purple-950/50 rounded text-2xs leading-4 px-1">
            /edit
          </span>
        </div>

        <div className="col-span-full">
          <TextareaAutosizeInput
            name="image-edit-prompt"
            value={prompt}
            onChange={onPromptChange}
            i18nPlaceholder={modelViewersKeys.lifestylePromptPlaceholder}
            maxRows={5}
            className="min-h-7 max-h-40 w-full resize-none border-none bg-transparent focus:ring-0 focus:outline-none pl-3 pr-2 pt-2 pb-0 text-sm leading-snug text-gray-700 dark:text-gray-300 placeholder-gray-500 dark:placeholder-gray-500"
          />
        </div>

        {generateError && (
          <p className="col-span-full px-3 pb-1 text-xs text-red-500 dark:text-red-400">
            {generateError}
          </p>
        )}

        <div className="flex flex-wrap gap-0.5 flex-1 items-center pl-2 min-h-10">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={onReferenceFileChange}
          />
          <PureIconButtonHelper
            icon={IconsEnum.PHOTOGRAPH_OUTLINE}
            iconClassName="h-5 w-5"
            i18nText={modelViewersKeys.addReference}
            className="py-1 pl-1 pr-2 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 focus:ring-offset-0 transition-colors duration-150 ease-out"
            onClick={() => fileInputRef.current?.click()}
          />

          {references.map((item) => (
            <div
              key={item.id}
              className="relative w-7 h-7 rounded overflow-hidden border dark:border-gray-700 flex-shrink-0 group"
            >
              {item.isUploading ? (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <PureIconButtonHelper
                  icon={IconsEnum.X_OUTLINE}
                  iconClassName="h-3 w-3 text-white"
                  className="absolute inset-0 bg-black/50 items-center justify-center z-10 hidden group-hover:flex"
                  onClick={() => onRemoveReference(item.id)}
                />
              )}
              <StaticImageHelper
                src={item.localUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        <div className="flex shrink-0 justify-end items-center h-10 pr-1.5">
          <ButtonHelper
            disabled={isDisabled || !prompt.trim()}
            onClick={onGenerate}
            className={cl(
              'py-1 pl-1 pr-2 space-x-1 rounded-md inline-flex items-center text-sm font-medium leading-5 focus:ring-base text-white border-cosmos animate-gradient-angle shadow-sm hover:shadow-md transition-colors duration-150 ease-out',
              isDisabled || !prompt.trim()
                ? 'bg-black/40 dark:bg-black/60 cursor-not-allowed'
                : 'bg-white/15 dark:bg-black/70 hover:bg-white/5 dark:hover:bg-black/25'
            )}
          >
            {isGenerating ? (
              <LoadingSpinner size="md" />
            ) : (
              <Icon className="h-5 w-5" icon={IconsEnum.SPARKLES_SOLID} />
            )}
            <span>
              <Translate
                id={
                  isGenerating
                    ? modelViewersKeys.generatingLifestyle
                    : words.create
                }
              />
            </span>
          </ButtonHelper>
        </div>
      </div>
    </div>
  );
}

export default ModelViewerImageEditPromptCard;
